import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useDebouncedCallback } from 'use-debounce';
import { useLocation, useNavigate } from 'react-router-dom';

import CommonImage from '@components/CommonImage';
import LoaderComponent from '@components/LoaderComponent';

import FavouriteFoldersList from '@features/favourites/components/FavouriteFoldersList';
import MaterialFavourite from '@features/favourites/components/MaterialFavourite';
import SortingDropdown from '@features/favourites/components/SortingDropdown';
import {
	FAVOURITE_SELECT_VIEW,
	FAVOURITES_FOLDER_SORTING_OPTIONS
} from '@features/favourites/utils/favouriteUtil';

import { fetchFavouriteMaterials } from '@store/favourite/actions';
import { getFavouriteMaterials } from '@store/reducers';

import { getFolderListWithDetails } from '@services/favouriteService';

import { isPageReachBottom, printApiError } from '@utils/Util';

import { resetLikeCount } from '@/features/favourites/redux/favouriteSlice';
import FilterComponent from '@/features/filter/v2/FilterComponent';
import {
	allFilterPages,
	favouriteListFieldList,
	generateFavouriteListParams
} from '@/features/filter/static/filterUtil';
import { fetchDependentFilterList } from '@/services/commonService';
import Chip from '@/features/filter/Chip';
import { closeLoader, openLoader } from '@/store/actions/LoaderAction';
import { clearGlobalFilter } from '@/store/actions/FilterAction';

const Favourite = () => {
	const [isLoading, setIsLoading] = useState(false);

	const [favouriteFolders, setFavouriteFolders] = useState([]);
	const [isAddNewFolder, setIsAddNewFolder] = useState(false);
	const [materialsDataList, setMaterialsDataList] = useState({});
	const [selectedMaterialIdList, setSelectedMaterialIdList] = useState([]);
	const [allFavouritesForMaterials, setAllFavouritesForMaterials] = useState({});
	const [searchForFabric, setSearchForFabric] = useState('');
	const [showFilter, setShowFilter] = useState(false);
	const [selectedSortingOption, setSelectedSortingOption] = useState({
		code: 'NEWEST',
		title: 'Newest first'
	});
	const [search, setSearch] = useState('');
	const debounceSearch = useDebouncedCallback((text) => setSearch(text), 500);
	const location = useLocation();
	const navigate = useNavigate();
	const activeParam = new URLSearchParams(location.search).get('active');
	const [selectView, setSelectView] = useState(
		activeParam ? FAVOURITE_SELECT_VIEW[activeParam]?.id : FAVOURITE_SELECT_VIEW.NITEX.id
	);
	const filterData = useSelector((store) => store.filterFieldStore);

	useEffect(() => {
		dispatch(resetLikeCount());
	}, []);

	useEffect(() => {
		if (selectView === FAVOURITE_SELECT_VIEW.NITEX.id || selectView === FAVOURITE_SELECT_VIEW.BUYER.id) {
			fetchFavouriteFolders();
		}
	}, [selectedSortingOption?.code, filterData, selectView, search]);

	let favouritesForMaterials = useSelector(getFavouriteMaterials);

	const scrollRef = useRef({});
	const dispatch = useDispatch();

	useEffect(() => {
		initApiCall().finally(() => setIsLoading(false));
	}, [selectedSortingOption?.code]);

	useEffect(() => {
		if (favouritesForMaterials?.data?.length > 0) {
			setAllFavouritesForMaterials(favouritesForMaterials);
		}
		scrollRef.current = favouritesForMaterials;
	}, [favouritesForMaterials]);

	const fetchFavouriteFolders = async (page = 0) => {
		try {
			dispatch(openLoader());
			let params = `?page=${page}&size=20&sortBy=${selectedSortingOption?.code}`;
			let requestBody = generateFavouriteListParams(filterData);
			requestBody.organizationTypeList = [selectView];
			requestBody.search = search;
			const response = await getFolderListWithDetails(params, requestBody);
			if (page > 0) {
				setFavouriteFolders((prev) => ({
					...prev,
					data: [...prev?.data, ...response?.data]
				}));
			} else {
				setFavouriteFolders(response);
			}
			dispatch(closeLoader());
		} catch (error) {
			printApiError(error);
		} finally {
			dispatch(closeLoader());
		}
	};

	const getFilterOptions = () => {
		let params = generateFavouriteListParams(filterData, 0, 'id,desc');
		params.organizationTypeList = [selectView];
		delete params.sortBy;
		delete params.page;
		delete params.size;
		dispatch(fetchDependentFilterList({ type: 'FAVOURITE_LIST' }, '', 'POST', params));
	};

	useEffect(() => {
		if (showFilter) {
			getFilterOptions();
		}
	}, [filterData, showFilter, selectView]);

	const initApiCall = async () => {
		setIsLoading(true);
		await dispatch(fetchFavouriteMaterials(0, 30)).finally(() => {});
	};

	const handleSearchFolder = async (e) => {
		debounceSearch(e.target.value);
	};

	const onChangeAllCheckAndSearchForFabric = async (e) => {
		let { name, value } = e.target;
		if (name === 'searchForFabric') {
			setSearchForFabric(value);
			setIsLoading(true);
			dispatch(fetchFavouriteMaterials(0, 30, value)).finally(() => {
				setIsLoading(false);
			});
		}
	};

	const handleScroll = () => {
		if (isPageReachBottom()) {
			let { currentPage, totalElements, totalPages, data } = scrollRef.current;
			if (totalElements !== data.length && currentPage + 1 !== totalPages) {
				setIsLoading(true);
				dispatch(fetchFavouriteMaterials(currentPage + 1, 30)).finally(() => {
					setIsLoading(false);
				});
			}
		}
	};

	const handleTabClick = (tabId) => {
		if (tabId !== selectView) {
			setSelectView(tabId);
			navigate(`/favourite/list?active=${tabId}`, { replace: true });
			dispatch(clearGlobalFilter());
		}
	};

	useEffect(() => {
		document.addEventListener('scroll', handleScroll);
		return () => {
			document.removeEventListener('scroll', handleScroll);
		};
	}, []);

	const topFilterForStyle = () => {
		return (
			<div className="d-flex justify-content-between pb-1">
				<div className="filter-actions collection-details-filter favorites-filter">
					<div className="form-group size32 m-0 search-filter filter-with-animation">
						<div className={'with-icon right-icon'}>
							<input
								type="text"
								placeholder="Folder name"
								name="searchForStyle"
								onChange={handleSearchFolder}
								autoComplete="off"
								autoFocus={false}
							/>
							<span className="right-icon">
								<CommonImage src="/icons/search.png" alt="search" />
							</span>
						</div>
					</div>

					<SortingDropdown
						onSelectOption={setSelectedSortingOption}
						selectedOption={selectedSortingOption?.title}
						sortingOptions={FAVOURITES_FOLDER_SORTING_OPTIONS}
					/>
				</div>
				<div className="add-new-design">
					<button className="button text size32 px-0" onClick={() => setIsAddNewFolder(true)}>
						<CommonImage src="/icons/add-round.svg" alt="add" />
						<span className="ml-1">Folder</span>
					</button>
				</div>
			</div>
		);
	};

	const topFilterForFabric = () => {
		return (
			<div className="filter-actions">
				<div className="form-group size32 m-0 search-filter filter-with-animation">
					<div className={'with-icon right-icon'}>
						<input
							type="text"
							autoComplete="off"
							name="searchForFabric"
							placeholder={'Name & Ref number'}
							onChange={onChangeAllCheckAndSearchForFabric}
						/>
						<span className="right-icon">
							<CommonImage className="cursor-pointer" src={`/icons/search.png`} alt="search" />
						</span>
					</div>
				</div>
			</div>
		);
	};

	return (
		<LoaderComponent loading={isLoading}>
			<div className="top-header bg-white">
				<div className="page-title-area pb-2">
					<h1 className="semibold-16">FAVOURITE</h1>
				</div>
				<div className="navigation-tab">
					<ul>
						{Object.values(FAVOURITE_SELECT_VIEW).map((view) => (
							<li
								key={view.id}
								className={selectView === view.id ? 'active' : ''}
								onClick={() => handleTabClick(view.id)}
							>
								{view.name}
							</li>
						))}
					</ul>
				</div>
				<div className="filter-container-top d-block">
					<div className="d-flex align-items-center justify-content-between">
						<div className="filters">
							{selectView !== FAVOURITE_SELECT_VIEW.FABRIC.id && (
								<>
									<div
										className="filter-toggle no-after pl-0"
										onClick={() => setShowFilter(!showFilter)}
									>
										<CommonImage src={`/icons/filter24.svg`} alt="filter" />
										<span>Filter</span>
									</div>
									<span className="items-count semibold-14 gray_dark_02 mr-4 mt-1">
										{favouriteFolders?.totalElements} Items
									</span>
								</>
							)}
						</div>
						{selectView === FAVOURITE_SELECT_VIEW.FABRIC.id
							? topFilterForFabric()
							: topFilterForStyle()}
					</div>

					<Chip />
				</div>
			</div>

			{selectView === FAVOURITE_SELECT_VIEW.FABRIC.id ? (
				<MaterialFavourite
					setIsLoading={setIsLoading}
					isLoading={isLoading}
					materialsDataList={materialsDataList}
					setMaterialsDataList={setMaterialsDataList}
					materialsData={allFavouritesForMaterials}
					setAllFavouritesForMaterials={setAllFavouritesForMaterials}
					selectedMaterialIdList={selectedMaterialIdList}
					setSelectedMaterialIdList={setSelectedMaterialIdList}
					search={searchForFabric}
				/>
			) : (
				<section className="filter-data-container filter-v3-data-container under-inner-tabs py-0">
					<FilterComponent
						filterPageName={allFilterPages.FAVOURITE_LIST}
						fields={
							selectView === FAVOURITE_SELECT_VIEW.BUYER.id
								? { ...favouriteListFieldList, mineAndShared: false }
								: favouriteListFieldList
						}
						showFilter={showFilter}
						isDependentFilter={true}
					/>
					<div className="data-table">
						<FavouriteFoldersList
							favouriteFolders={favouriteFolders}
							fetchFavouriteFolders={fetchFavouriteFolders}
							isAddNewFolder={isAddNewFolder}
							setIsAddNewFolder={setIsAddNewFolder}
						/>
					</div>
				</section>
			)}
		</LoaderComponent>
	);
};

export default Favourite;
